import { api } from '../../lib/api';
import type {
  AudiencePreviewResult,
  AudienceScope,
  Broadcast,
  BroadcastChannel,
  BroadcastContentPreview,
  BroadcastDraftInput,
  BroadcastSenderMode,
} from '../../types/broadcast';
import type { BroadcastSend } from '../../types/broadcastSend';

// Client API for the broadcasts module (Stage 6A v2). Audience preview takes the
// new audienceScope; ?details=true returns the full recipient list (modal).

export async function previewAudience(
  scope: AudienceScope,
  details = false,
): Promise<AudiencePreviewResult> {
  return api<AudiencePreviewResult>(
    `/api/broadcasts/preview-audience${details ? '?details=true' : ''}`,
    { method: 'POST', body: JSON.stringify(scope) },
  );
}

export type PreviewContentRequest = {
  audienceScope: AudienceScope;
  channels: BroadcastChannel[];
  emailSubject: string;
  emailBodyMarkdown: string;
  smsBody: string;
  emailFromMode: BroadcastSenderMode;
  emailFromCustomName: string | null;
  emailReplyToMode: BroadcastSenderMode;
  emailReplyToCustomEmail: string | null;
  contactId: string;
};

export async function previewContent(
  input: PreviewContentRequest,
): Promise<BroadcastContentPreview> {
  return api<BroadcastContentPreview>('/api/broadcasts/preview-content', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ---------- CRUD ----------

export async function listBroadcasts(): Promise<Broadcast[]> {
  const res = await api<{ broadcasts: Broadcast[] }>('/api/broadcasts');
  return res.broadcasts;
}

export async function getBroadcast(id: string): Promise<Broadcast> {
  const res = await api<{ broadcast: Broadcast }>(`/api/broadcasts/${id}`);
  return res.broadcast;
}

export async function createBroadcast(input: BroadcastDraftInput): Promise<Broadcast> {
  const res = await api<{ broadcast: Broadcast }>('/api/broadcasts', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return res.broadcast;
}

export async function updateBroadcast(
  id: string,
  input: BroadcastDraftInput,
): Promise<Broadcast> {
  const res = await api<{ broadcast: Broadcast }>(`/api/broadcasts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return res.broadcast;
}

// v6 — DELETE returns the kind performed: 'hard' (draft removed) or 'soft' (sent
// broadcast hidden, data preserved). The client tailors its toast accordingly.
export async function deleteBroadcast(id: string): Promise<{ kind: 'hard' | 'soft' }> {
  const res = await api<{ ok: true; kind: 'hard' | 'soft' }>(`/api/broadcasts/${id}`, {
    method: 'DELETE',
  });
  return { kind: res.kind };
}

// v6 admin recovery — restore a soft-deleted broadcast.
export async function restoreBroadcast(id: string): Promise<Broadcast> {
  const res = await api<{ broadcast: Broadcast }>(`/api/broadcasts/${id}/restore`, {
    method: 'POST',
  });
  return res.broadcast;
}

// v6 admin recovery — list soft-deleted broadcasts.
export async function listDeletedBroadcasts(): Promise<Broadcast[]> {
  const res = await api<{ broadcasts: Broadcast[] }>('/api/broadcasts/deleted');
  return res.broadcasts;
}

export async function duplicateBroadcast(id: string): Promise<Broadcast> {
  const res = await api<{ broadcast: Broadcast }>(`/api/broadcasts/${id}/duplicate`, {
    method: 'POST',
  });
  return res.broadcast;
}

export async function sendBroadcast(
  id: string,
): Promise<{ broadcast: Broadcast; dispatched: number }> {
  return api<{ broadcast: Broadcast; dispatched: number }>(`/api/broadcasts/${id}/send`, {
    method: 'POST',
  });
}

export async function listBroadcastSends(id: string): Promise<BroadcastSend[]> {
  const res = await api<{ sends: BroadcastSend[] }>(`/api/broadcasts/${id}/sends`);
  return res.sends;
}
